import { create } from 'zustand';

import type { ServerAccess } from '@core/access/server-access';

/**
 * Replaces the `BehaviorSubject` half of `UserService`.
 *
 * The signed-in user, their AAD group-derived roles, and the role they are
 * currently acting as. The toolbar's role picker writes `currentRole`, which
 * is why this is shared state and not a field on either component.
 *
 * Nothing outside this file should read `roles` to decide what a user may do.
 * `@core/access` turns them into capabilities, and the shell, the routes and
 * the screens all ask it rather than testing for a role name - which is what
 * the nav filter used to do, and is why a renamed nav item could change who
 * could see it.
 *
 * The Graph HTTP calls that also lived on `UserService` are in
 * `src/core/api/graph.api.ts`; this file is state only.
 */

export interface UserGroup {
  groupName: string | null;
  groupId: string;
  role: string;
}

export interface UserData {
  displayName: string;
  email: string;
  userId: string;
  groups: UserGroup[];
  roles: string[];
  currentRole: string;
  /**
   * Two-letter region / country code from the sign-in claims, upper case.
   *
   * It chooses which market's rules apply - the postal code format, what a
   * property category makes mandatory, whether a request type is chargeable -
   * through the profile in `@features/valuations/profiles`. It no longer
   * decides whether a module is available: the Singapore screens used to be
   * registered only for `SG` users, and what a person may see is a capability
   * now, in `@core/access`.
   *
   * Null when the tenant sent no country claim, which means the standard
   * profile rather than no access.
   */
  region: string | null;
  /**
   * The API's resolved access (`GET /api/v1/me`), once it has answered. While
   * present it decides capabilities; `roles` is the fallback until then, or if
   * the endpoint is unavailable.
   */
  access?: ServerAccess | null;
}

export interface UserState {
  userData: UserData | null;
  setUserData: (data: UserData | null) => void;
  /** The toolbar's role picker - changes the acting role, not the granted ones. */
  setCurrentRole: (role: string) => void;
}

export const useUserStore = create<UserState>((set) => ({
  userData: null,

  setUserData: (data) => set({ userData: data }),

  setCurrentRole: (role) =>
    set((state) => (state.userData ? { userData: { ...state.userData, currentRole: role } } : state))
}));

export const userStore = {
  getUserData: () => useUserStore.getState().userData,
  setUserData: (data: UserData | null) => useUserStore.getState().setUserData(data)
};

/**
 * AAD group object id -> portal role.
 *
 * Owned by the toolbar, which is the only writer. It stays a plain
 * map rather than moving to config because the ids are tenant constants, not
 * per-environment settings - dev, UAT and prod all resolve the same groups.
 */
export const groupRoleMap: Record<string, string> = {
  '136c286a-6d25-4332-8433-8f51b3c1c5b8': 'Appraiser',
  'c514850a-ae7f-4c60-a41c-2ff5787d46ca': 'Reader',
  '137878c4-e3f7-47c4-9070-84b046567ca7': 'GlobalAdmin',
  '5729de64-a53a-48b5-a7a9-8949acc93a4e': 'GlobalReader',
  'b17c89fe-c84d-40b5-8b84-dc7a2f758758': 'User',
  'd3c6b668-34a9-42af-9b2a-e124e216bc9c': 'PortfolioCoordinator',
  '120d0a50-26a8-463c-9b54-f55bea83efa8': 'Ratings'
};

/** Most to least privileged. The first match wins in `getHighestRole`. */
const ROLE_ORDER = ['GlobalAdmin', 'PortfolioCoordinator', 'Appraiser', 'User', 'GlobalReader', 'Reader'];

export function getHighestRole(userRoles: string[]): string {
  for (const role of ROLE_ORDER) {
    if (userRoles.includes(role)) {
      return role;
    }
  }

  return 'User';
}

/** Roles each role implicitly carries, used for permission checks. */
export const roleHierarchy: Record<string, string[]> = {
  GlobalAdmin: ['PortfolioCoordinator', 'Appraiser', 'Ratings', 'User', 'GlobalReader', 'Reader'],
  PortfolioCoordinator: ['Appraiser', 'Ratings', 'User', 'GlobalReader', 'Reader'],
  Appraiser: ['Ratings', 'User'],
  Ratings: [],
  User: [],
  GlobalReader: ['Reader'],
  Reader: []
};

/** Anything of this shape is an AAD object id rather than a role name. */
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Builds the `UserData` shape from an MSAL account plus the claim values the
 * toolbar collected - group object ids and app role names, in one list.
 *
 * The two are told apart by shape. A GUID is a group and resolves through
 * `groupRoleMap`; anything else is an Entra **app role**, which arrives as its
 * own name and is taken as the role. Before this, non-GUID values were
 * dropped, so a role assigned as an app role rather than through a group
 * reached the app as no role at all - which is how the `IVS.*` roles would
 * have arrived, since they are granted per user rather than per office group.
 */
export function buildUserData(
  displayName: string,
  email: string,
  userId: string,
  claimValues: string[],
  region?: string | null
): UserData {
  const groups: UserGroup[] = claimValues.flatMap((value) => {
    if (!GUID.test(value)) {
      return [];
    }

    const role = groupRoleMap[value];
    return role ? [{ groupName: null, groupId: value, role }] : [];
  });

  const appRoles = claimValues.filter((value) => !GUID.test(value));
  const roles = [...new Set([...groups.map((group) => group.role), ...appRoles])];

  return {
    displayName,
    email,
    userId,
    groups,
    roles,
    currentRole: getHighestRole(roles),
    region: region ? region.trim().toUpperCase() : null
  };
}
